import { useRef, useState } from 'react';

/**
 * Перетаскивание строк списка за ручку. Общее для категорий и услуг: логика
 * одна, а две копии разъезжаются на первой же правке.
 *
 * Порядок во время перетаскивания живёт здесь, а не на сервере: строки должны
 * меняться местами под пальцем, а не после ответа. Наверх он уезжает один раз,
 * когда палец отпущен, — целым списком идентификаторов.
 */
export function useSortable(ids: number[], commit: (next: number[]) => void, rowSelector: string) {
  const [order, setOrder] = useState<number[] | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const drag = useRef<{ from: number; to: number; startY: number; rowH: number; ids: number[] } | null>(null);

  // Свой порядок показываем, только пока он описывает тот же набор строк:
  // фоновый перезапрос мог добавить услугу или убрать её, и тогда он неверен.
  const same = order?.length === ids.length && order.every((id) => ids.includes(id));
  const list = same && order ? order : ids;

  function start(e: React.PointerEvent, id: number) {
    const row = (e.currentTarget as HTMLElement).closest(rowSelector) as HTMLElement | null;
    const from = list.indexOf(id);
    if (!row || from < 0) return;
    // Захват пальца: без него движение за пределы ручки теряется. Старые
    // webview'ы умеют бросить отсюда исключение — перетаскивание из-за этого
    // ломаться не должно.
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* обойдёмся без захвата */
    }
    drag.current = { from, to: from, startY: e.clientY, rowH: row.offsetHeight + 8, ids: list };
    setDragId(id);
  }

  function move(e: React.PointerEvent) {
    const d = drag.current;
    if (!d) return;
    // Цель считаем от исходной позиции, а не от предыдущей: так порядок не
    // «убегает» от пальца, когда строки под ним меняются местами.
    const shift = Math.round((e.clientY - d.startY) / d.rowH);
    const to = Math.max(0, Math.min(d.ids.length - 1, d.from + shift));
    if (to === d.to) return;
    d.to = to;
    setOrder(moved(d.ids, d.from, to));
  }

  function end() {
    const d = drag.current;
    drag.current = null;
    setDragId(null);
    if (!d || d.to === d.from) return;
    commit(moved(d.ids, d.from, d.to));
  }

  /** Вернуться к серверному порядку: после успешного сохранения или после отказа. */
  const clear = () => setOrder(null);

  /** Обработчики вешаются на ручку — саму строку тащить нельзя, иначе не проскроллить список. */
  const gripProps = (id: number) => ({
    onPointerDown: (e: React.PointerEvent) => start(e, id),
    onPointerMove: move,
    onPointerUp: end,
    onPointerCancel: end,
  });

  return { list, dragId, gripProps, clear };
}

function moved(ids: number[], from: number, to: number): number[] {
  const next = [...ids];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
}
